import moment from 'moment-timezone';
import { ProColumns } from '@ant-design/pro-table';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '../../../../Common/Table';
import { IAllViewed, IUserViews, useGetAllViews } from '../../../../../hooks/userViews';
import './styles.less';

const ExpandedRow = ({ record }: { record: IAllViewed }) => (
  <Table<IUserViews>
    columns={[
      { title: 'Viewer Name', dataIndex: 'name', key: 'name' },
      {
        title: 'Viewer Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Viewed At',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (dom: React.ReactNode) =>
          typeof dom === 'string' ? moment(dom).format('ddd DD MMM, YYYY hh:mm a') : dom,
      },
    ]}
    className="custom-expanded-row-content"
    dataSource={record.UserViews}
    pagination={false}
    showSorterTooltip={false}
    rowKey="id"
    toolBarRender={false}
    search={false}
    size='small'
  />
);

interface IAllViewedTableProps {
  size: 'small' | 'middle' | 'large';
}

const AllViewedTable = ({ size }: IAllViewedTableProps) => {
  const navigate = useNavigate();
  const { fetch } = useGetAllViews();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleExpand = (expanded: boolean, record: IAllViewed) => {
    const uniqueKey = `${record.id}-${record.createdAt}`;

    setExpandedRowKeys((prevExpandedRowKeys) =>
      expanded
        ? [...prevExpandedRowKeys, uniqueKey]
        : prevExpandedRowKeys.filter((key) => key !== uniqueKey)
    );
  };

  const tableRequest = async (): Promise<{ data: IAllViewed[]; success: boolean; total: number }> => {
    setLoading(true);

    try {
      const res = await fetch();

      if (res?.data) {
        const sortedData = res.data.viewedReports.sort((a, b) => {
          const aTimestamp = a.createdAt;
          const bTimestamp = b.createdAt;

          return moment(bTimestamp).valueOf() - moment(aTimestamp).valueOf();
        });

        return { data: sortedData, success: true, total: sortedData.length };
      }

      return { data: [], success: false, total: 0 };
    } finally {
      setLoading(false);
    }
  };

  const columns: ProColumns<IAllViewed>[] = [
    {
      title: 'Report Name',
      dataIndex: 'name',
      sorter: false,
      render: (dom, entity) => {
        const navigateToReport = () => {
          navigate(`/reports/${entity.id}/edit`);
        }

        return (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <p
            onClick={navigateToReport}
            style={{ cursor: 'pointer' }}
          >
            {dom}
          </p>
        );
      }
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      render: (dom: React.ReactNode) =>
        typeof dom === 'string' ? moment(dom).format('ddd DD MMM, YYYY hh:mm a') : dom,
      sorter: false,
    },
    {
      title: 'Total Views',
      dataIndex: 'UserViews',
      render: (_: React.ReactNode, entity) => entity.UserViews.length,
      sorter: false,
    },
  ];

  const expandable = {
    expandedRowKeys,
    onExpand: handleExpand,
    // eslint-disable-next-line react/no-unstable-nested-components
    expandedRowRender: (record: IAllViewed) => <ExpandedRow record={record} />,
  };

  return (
    <Table<IAllViewed>
      size={size}
      search={false}
      columns={columns}
      loading={loading}
      expandable={expandable}
      showSorterTooltip={false}
      toolBarRender={false}
      pagination={false}
      rowKey={(record) => `${record.id}-${record.createdAt}`}
      request={tableRequest}
    />
  );
};

export default AllViewedTable;
